import {Drawer, Spin, Table} from "antd";

import 'json-diff-kit/dist/viewer.css';
import {Differ, Viewer} from "json-diff-kit";
import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import VulSourceItem from "../../domain/VulSourceItem";
import VulSourceItemChangeLog from "../../domain/VulSourceItemChangeLog";
import Column from "antd/es/table/Column";
import {formatDateTime} from "../../utils/FormatUtils";
import * as styles from "./VulSourceChangeLogListDrawer.module.css";
import {VulSourceItemServiceContext} from "../../Contexts";

const differ = new Differ({
    detectCircular: true,    // default `true`
    maxDepth: Infinity,      // default `Infinity`
    showModifications: true, // default `true`
    arrayDiffMethod: 'lcs',  // default `"normal"`, but `"lcs"` may be more useful
});

const diffViewerProps = {
    indent: 4,
    highlightInlineDiff: true,
    hideUnchangedLines: true,
};

interface Props {
    item?: VulSourceItem;
    changeLogs?: VulSourceItemChangeLog[];
    open: boolean;
    onClose: () => void;
}

type LoadChangeLogData = (changeLog: VulSourceItemChangeLog) => Promise<VulSourceItemChangeLog | undefined>;

function VulSourceChangeLogListDrawer(props: Props) {
    const vulSourceItemService = useContext(VulSourceItemServiceContext);
    const changeLogDataCacheRef = useRef<Map<string, Promise<VulSourceItemChangeLog | undefined>>>(new Map());

    useEffect(() => {
        changeLogDataCacheRef.current.clear();
    }, [props.item?.id]);

    const loadChangeLogData = useCallback((changeLog: VulSourceItemChangeLog): Promise<VulSourceItemChangeLog | undefined> => {
        if (!props.item?.id || !changeLog.id) {
            return Promise.resolve(undefined);
        }

        if (isChangeLogDataLoaded(changeLog)) {
            return Promise.resolve(changeLog);
        }

        const cached = changeLogDataCacheRef.current.get(changeLog.id);

        if (cached) {
            return cached;
        }

        const promise = vulSourceItemService.getChangeLogData(props.item.id, changeLog.id)
            .then(data => Object.assign(new VulSourceItemChangeLog(), changeLog, {
                jsonDataRaw: data.jsonDataRaw,
                jsonData: data.jsonData,
                attributesRaw: data.attributesRaw,
                attributes: data.attributes
            }))
            .catch(() => {
                changeLogDataCacheRef.current.delete(changeLog.id);

                return undefined;
            });

        changeLogDataCacheRef.current.set(changeLog.id, promise);

        return promise;
    }, [props.item?.id, vulSourceItemService]);

    return (
        <Drawer title={"Vulnerability item changes"} destroyOnHidden={true} rootClassName={styles.drawer} open={props.open} onClose={props.onClose}>
            <Table
                showSorterTooltip={false}
                dataSource={props.changeLogs?.slice(0, -1)}
                size="middle"
                showHeader={false}
                pagination={false}
                rowKey="id">

                <Column
                    dataIndex={"difference"}
                    title={"Difference"}
                    render={(value, record: VulSourceItemChangeLog) => props.changeLogs
                        ? <ChangeLogDifference changeLogs={props.changeLogs} changeLog={record} loadChangeLogData={loadChangeLogData}/>
                        : null}
                />

            </Table>
        </Drawer>
    );

    function isChangeLogDataLoaded(changeLog: VulSourceItemChangeLog) {
        return changeLog.attributesRaw !== undefined;
    }
}

interface ChangeLogDifferenceProps {
    changeLogs: VulSourceItemChangeLog[];
    changeLog: VulSourceItemChangeLog;
    loadChangeLogData: LoadChangeLogData;
}

function ChangeLogDifference(props: ChangeLogDifferenceProps) {
    const olderChangeLog = VulSourceItem.findOlderChangeLog(props.changeLogs, props.changeLog);
    const [loadedOlderChangeLog, setLoadedOlderChangeLog] = useState<VulSourceItemChangeLog>();
    const [loadedChangeLog, setLoadedChangeLog] = useState<VulSourceItemChangeLog>();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (olderChangeLog === undefined) {
            return;
        }

        let cancelled = false;

        setLoading(true);
        setLoadedOlderChangeLog(undefined);
        setLoadedChangeLog(undefined);

        Promise.all([
            props.loadChangeLogData(olderChangeLog),
            props.loadChangeLogData(props.changeLog)
        ]).then(([olderValue, value]) => {
            if (cancelled) {
                return;
            }

            setLoadedOlderChangeLog(olderValue);
            setLoadedChangeLog(value);
        }).finally(() => {
            if (!cancelled) {
                setLoading(false);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [olderChangeLog?.id, props.changeLog.id, props.loadChangeLogData]);

    if (olderChangeLog === undefined) {
        return <i>no difference available</i>;
    }

    return <>
        <div className={styles.timestamps}>
            <span><b>Timestamp:</b> <i>{formatDateTime(olderChangeLog.timestamp)}</i></span>
            <span><b>Timestamp:</b> <i>{formatDateTime(props.changeLog.timestamp)}</i></span>
        </div>
        {renderDiff()}
    </>;

    function renderDiff() {
        if (loading) {
            return <Spin/>;
        }

        if (!loadedOlderChangeLog || !loadedChangeLog) {
            return <i>failed to load difference</i>;
        }

        return <Viewer className={styles.diff} diff={differ.diff(loadedOlderChangeLog.attributes, loadedChangeLog.attributes)} {...diffViewerProps}/>;
    }
}

export default VulSourceChangeLogListDrawer;
